import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  WEATHER,
  WEATHER_FX,
  type WeatherKind,
} from '../config/GameConfig';

/**
 * WeatherManager —— 战场天气
 *
 * 在 _onTierUp 调 `rollNext()` 滚一种天气，持续到下一波
 * （= 下一次 rollNext 时被覆盖）。
 *
 * 各天气的"效果"由外部读 getter 主动应用：
 *   - wind     →  返回 windX (px/s)，由 MainScene 给 bullet/bossBullet/coin 持续叠到 velocity
 *   - magnet-fail → coinMagnetEnabled() = false；fireRateMul = 0.85
 *   - meteor   →  WeatherManager 自己 tick spawnMeteor 回调
 *   - goldRush →  coinMul / spawnIntervalMul 给 Spawner 与金币掉落参考
 *
 * 视觉：进入新天气时弹一个横幅（hud.showWeatherBanner），
 *       屏幕四周有一层颜色 vignette（this._vignette）。
 */
export default class WeatherManager {
  scene: Phaser.Scene;
  current: WeatherKind = 'none';
  /** 风向：1 = 向右 / -1 = 向左；每 switchMs 翻一次 */
  private _windDir: 1 | -1 = 1;
  private _windSwitchAcc = 0;
  /** 陨石积累计时器 */
  private _meteorAcc = 0;
  /** 屏角 vignette 装饰 */
  private _vignette: Phaser.GameObjects.Rectangle | null = null;
  /** 暂停状态 */
  private _paused = false;

  /** 天气切换时的副作用回调（横幅 / 音效 / spawn 节奏） */
  onChange: ((kind: WeatherKind) => void) | null = null;
  /** 陨石产生回调：参数为 (x, y) ，由 MainScene 决定怎么落（复用 BossProjectile） */
  onMeteor: ((x: number, y: number) => void) | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * 滚一次天气 —— 通常在 difficulty.onTierUp 调用
   * 前 2 波（tier 0/1）不滚，留个安静的开局让玩家适应基本操作
   */
  rollNext(tier: number): void {
    if (tier <= 1) {
      this._setWeather('none');
      return;
    }
    const total = Object.values(WEATHER).reduce((s, w) => s + w.weight, 0);
    let r = Math.random() * total;
    for (const k of Object.keys(WEATHER) as WeatherKind[]) {
      r -= WEATHER[k].weight;
      if (r <= 0) {
        this._setWeather(k);
        return;
      }
    }
    this._setWeather('none');
  }

  /** 显式设定天气（debug 或关卡模式 Final 用） */
  setWeather(kind: WeatherKind): void {
    this._setWeather(kind);
  }

  private _setWeather(kind: WeatherKind): void {
    if (kind === this.current) return;
    this.current = kind;
    this._windDir = Math.random() < 0.5 ? -1 : 1;
    this._windSwitchAcc = 0;
    this._meteorAcc = 0;
    this._refreshVignette();
    this.onChange?.(kind);
  }

  pause(): void { this._paused = true; }
  resume(): void { this._paused = false; }

  /**
   * 每帧由 MainScene 调用：陨石计时 / 风向翻转
   */
  update(delta: number): void {
    if (this._paused || this.current === 'none') return;

    if (this.current === 'wind') {
      this._windSwitchAcc += delta;
      if (this._windSwitchAcc >= WEATHER_FX.wind.switchMs) {
        this._windSwitchAcc = 0;
        this._windDir = (this._windDir === 1 ? -1 : 1);
      }
    }

    if (this.current === 'meteor' && this.onMeteor) {
      this._meteorAcc += delta;
      while (this._meteorAcc >= WEATHER_FX.meteor.intervalMs) {
        this._meteorAcc -= WEATHER_FX.meteor.intervalMs;
        // 屏侧 ±20px 抖动 +- 屏幕中段任意 y，让陨石看着不是一条线下来
        const fromLeft = Math.random() < 0.5;
        const x = fromLeft
          ? Phaser.Math.Between(20, GAME_WIDTH * 0.4)
          : Phaser.Math.Between(GAME_WIDTH * 0.6, GAME_WIDTH - 20);
        this.onMeteor(x, -30);
      }
    }
  }

  /** 当前是否启用金币磁吸 */
  coinMagnetEnabled(): boolean {
    return this.current !== 'magnet-fail';
  }

  /** 玩家射击间隔的天气倍率（< 1 = 更快） */
  fireRateMul(): number {
    return this.current === 'magnet-fail' ? WEATHER_FX.magnetFail.fireRateMul : 1;
  }

  /** 这一帧给所有非玩家弹药施加的 x 风力（px/s） */
  windX(): number {
    if (this.current !== 'wind') return 0;
    return this._windDir * WEATHER_FX.wind.strength;
  }

  /** 金币倍率 / 刷怪间隔倍率（goldRush） */
  coinMul(): number {
    return this.current === 'goldRush' ? WEATHER_FX.goldRush.coinMul : 1;
  }
  spawnIntervalMul(): number {
    return this.current === 'goldRush' ? WEATHER_FX.goldRush.spawnIntervalMul : 1;
  }

  /** 屏幕四周一层薄薄的颜色 vignette，提示当前天气 */
  private _refreshVignette(): void {
    if (this._vignette) {
      this._vignette.destroy();
      this._vignette = null;
    }
    if (this.current === 'none') return;
    const tint = WEATHER[this.current].tint;
    this._vignette = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT)
      .setStrokeStyle(20, tint, 0.18)
      .setDepth(95)
      .setScrollFactor(0)
      .setActive(true);
  }

  destroy(): void {
    this._vignette?.destroy();
    this._vignette = null;
  }
}
